/**
 * QL-287 — One account of the Account Cash Overview, with its portfolios
 * expanded underneath. The expanded rows reuse the account table's columns so
 * each portfolio's net buy / sell sits in the same right-aligned column as the
 * account's net portfolio total.
 */

import type {
  AccountCashOverviewRow,
  CashAccount,
  PortfolioType,
  Translated,
} from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { TableCell, TableHead, TableRow } from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./account-cash-row.i18n";
import {
  type FormatNumberFn,
  formatDeposit,
  formatPlannedMonth,
  formatSigned,
  formatWhole,
} from "./types";

type AccountCashRowProps = {
  row: AccountCashOverviewRow;
  expanded: boolean;
  onToggle: () => void;
  language: string;
  fmt: FormatNumberFn;
};

const NUMERIC = "text-right tabular-nums whitespace-nowrap";
const SUB_HEAD = "py-2 text-xs font-medium text-muted-foreground";

/** Net trade: unsigned amount with a buy / sell suffix; zero stays a plain 0. */
function formatNetTrade(value: number, fmt: FormatNumberFn, t: Translated<typeof T>) {
  const rounded = Math.round(value);
  if (rounded === 0) return formatWhole(0, fmt);
  return `${formatWhole(Math.abs(rounded), fmt)} ${rounded > 0 ? t.buy : t.sell}`;
}

function portfolioTypeLabel(type: PortfolioType, t: Translated<typeof T>): string {
  return type === "model" ? t.model : type === "dynamic" ? t.dynamic : t.custom;
}

/** Sub-line under an account name: "G1 · EUR", "No goal · EUR", "… · New account". */
function accountSubLine(account: CashAccount, t: Translated<typeof T>): string {
  const parts = [account.goalName ?? t.noGoal, account.currency];
  if (account.isNew) parts.push(t.newAccount);
  return parts.join(" · ");
}

export function AccountCashRow({ row, expanded, onToggle, language, fmt }: AccountCashRowProps) {
  const t = useT(T);
  const { account, portfolios } = row;
  const canExpand = portfolios.length > 0;
  const toggleLabel = `${expanded ? t.collapse : t.expand}: ${account.name}`;

  return (
    <>
      <TableRow data-testid="account-cash-row">
        <TableCell>
          <div className="flex items-start gap-2">
            {canExpand ? (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                aria-label={toggleLabel}
                className="mt-0.5 text-muted-foreground hover:text-foreground"
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="w-4 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-medium">{account.name}</div>
              <div className="text-xs text-muted-foreground">{accountSubLine(account, t)}</div>
              {row.conversionFailed && (
                <div className="text-xs text-muted-foreground">{t.conversionFailed}</div>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell className="tabular-nums">
          {canExpand ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={`${portfolios.length} — ${toggleLabel}`}
              className="underline-offset-4 hover:underline"
            >
              {portfolios.length}
            </button>
          ) : (
            0
          )}
        </TableCell>
        <TableCell className={NUMERIC}>{formatWhole(account.cashAmount, fmt)}</TableCell>
        <TableCell className={NUMERIC}>{formatNetTrade(row.netPortfolioAmount, fmt, t)}</TableCell>
        <TableCell className={NUMERIC}>{formatSigned(row.transfersNet, fmt)}</TableCell>
        <TableCell className={NUMERIC}>{formatDeposit(row.depositNeeded, fmt)}</TableCell>
        <TableCell className={NUMERIC}>{formatWhole(row.cashLeft, fmt)}</TableCell>
      </TableRow>

      {expanded && canExpand && (
        <>
          <TableRow className="bg-muted/30">
            <TableHead scope="col" className={SUB_HEAD}>
              <div className="grid grid-cols-2 gap-4 pl-6">
                <span>{t.goal}</span>
                <span>{t.portfolio}</span>
              </div>
            </TableHead>
            <TableHead scope="col" className={SUB_HEAD}>
              {t.type}
            </TableHead>
            <TableHead scope="col" className={SUB_HEAD}>
              {t.plannedMonth}
            </TableHead>
            <TableHead scope="col" className={`${SUB_HEAD} text-right`}>
              {t.netBuySell}
            </TableHead>
            <TableCell colSpan={3} />
          </TableRow>
          {portfolios.map((p) => (
            <TableRow key={p.portfolioId} className="bg-muted/30">
              <TableCell className="py-2">
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <span className="truncate">{p.goalName}</span>
                  <span className="truncate">{p.portfolioName}</span>
                </div>
              </TableCell>
              <TableCell className="py-2">{portfolioTypeLabel(p.portfolioType, t)}</TableCell>
              <TableCell className="py-2 whitespace-nowrap">
                {formatPlannedMonth(p.plannedDate, language)}
              </TableCell>
              <TableCell className={`py-2 ${NUMERIC}`}>
                {formatNetTrade(p.netAmount, fmt, t)}
              </TableCell>
              <TableCell colSpan={3} />
            </TableRow>
          ))}
        </>
      )}
    </>
  );
}
